import { fromZonedDateTimeInput, toZonedDateTimeInput } from "@/libs/zonedDateTime";

const DAY_MS = 86_400_000;
export const MAX_PROPOSAL_SESSIONS = 24; // The authorized availability check accepts at most 24.
export const MAX_SCHEDULE_DAYS_AHEAD = 180;

export type SessionEntry = {
  id: string;
  localStart: string;
  exactStart?: string;
  duration: string;
  courtId: string;
  courtIds?: string[];
};

export type BatchDraft = {
  date: string;
  time: string;
  exactStart?: string;
  duration: string;
  courtId: string;
  repeat: "Does not repeat" | "Daily" | "Weekly" | "Custom";
  every: string;
  unit: "day" | "week";
  weekdays: number[];
  ends: "After occurrences" | "On date";
  occurrences: string;
  endDate: string;
};

export type ScheduledWindow = {
  id: string;
  localStart: string;
  starts_at: string;
  ends_at: string;
  court_ids: string[];
};

type Result<T> = { value: T; error: null } | { value: null; error: string };
const success = <T>(value: T): Result<T> => ({ value, error: null });
const failure = <T>(error: string): Result<T> => ({ value: null, error });

function civilDay(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const day = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(day) && new Date(day).toISOString().slice(0, 10) === value ? day : null;
}

function durationMinutes(value: string): number | null {
  const duration = Number(value);
  return Number.isSafeInteger(duration) && duration >= 15 && duration <= 480 ? duration : null;
}

function localDateAt(instant: Date, timezone: string): number | null {
  return civilDay(toZonedDateTimeInput(instant.toISOString(), timezone).slice(0, 10));
}

export function expandScheduleBatch(batch: BatchDraft, timezone: string, now: Date,
  remaining: number): Result<Omit<SessionEntry, "id">[]> {
  const start = civilDay(batch.date);
  const today = localDateAt(now, timezone);
  if (start === null || today === null || !/^([01]\d|2[0-3]):[0-5]\d$/.test(batch.time))
    return failure("Choose a valid session date and start time.");
  const lastAllowed = today + MAX_SCHEDULE_DAYS_AHEAD * DAY_MS;
  if (start < today || start > lastAllowed)
    return failure("Sessions must be within the next 180 days.");
  if (durationMinutes(batch.duration) === null)
    return failure("Duration must be between 15 and 480 minutes.");
  if (!batch.courtId) return failure("Choose a court or No court required.");
  if (remaining < 1) return failure(`A proposal can have at most ${MAX_PROPOSAL_SESSIONS} sessions.`);

  const repeated = batch.repeat !== "Does not repeat";
  const every = batch.repeat === "Custom" ? Number(batch.every) : 1;
  const unit = batch.repeat === "Daily" ? "day" : batch.repeat === "Weekly" ? "week" : batch.unit;
  if (repeated && (!Number.isSafeInteger(every) || every < 1 || every > 12))
    return failure("Repeat every must be between 1 and 12 days or weeks.");
  const count = repeated && batch.ends === "After occurrences" ? Number(batch.occurrences) : 1;
  if (repeated && batch.ends === "After occurrences" && (!Number.isSafeInteger(count) || count < 1 || count > remaining))
    return failure(`Choose 1 to ${remaining} occurrences for this batch.`);
  const end = repeated && batch.ends === "On date" ? civilDay(batch.endDate) : start;
  if (end === null || end < start || end > lastAllowed)
    return failure("Choose an end date after the first session and within 180 days.");
  const weekdays = batch.repeat === "Custom" && unit === "week"
    ? batch.weekdays : [new Date(start).getUTCDay()];
  if (repeated && unit === "week" && weekdays.length === 0)
    return failure("Choose at least one day to repeat on.");

  const startWeek = start - new Date(start).getUTCDay() * DAY_MS;
  const last = repeated && batch.ends === "On date" ? end : lastAllowed;
  const entries: Omit<SessionEntry, "id">[] = [];
  for (let day = start; day <= last; day += DAY_MS) {
    const weekday = new Date(day).getUTCDay();
    const week = (day - weekday * DAY_MS - startWeek) / (7 * DAY_MS);
    const matches = !repeated || (unit === "day" ? (day - start) / DAY_MS % every === 0
      : week % every === 0 && weekdays.includes(weekday));
    if (!matches) continue;
    const localStart = `${new Date(day).toISOString().slice(0, 10)}T${batch.time}`;
    const exactStart = day === start && batch.exactStart
      && toZonedDateTimeInput(batch.exactStart, timezone) === localStart ? batch.exactStart : undefined;
    if (!exactStart && !fromZonedDateTimeInput(localStart, timezone))
      return failure("A selected local time does not exist in the facility timezone.");
    entries.push({ localStart, exactStart, duration: batch.duration, courtId: batch.courtId });
    if (entries.length > remaining)
      return failure(`A proposal can have at most ${MAX_PROPOSAL_SESSIONS} sessions.`);
    if (!repeated || batch.ends === "After occurrences" && entries.length === count) break;
  }
  if (!entries.length || repeated && batch.ends === "After occurrences" && entries.length !== count)
    return failure("The recurrence has no valid dates in the next 180 days.");
  return success(entries);
}

export function materializeSessions(entries: SessionEntry[], timezone: string, now: Date): Result<ScheduledWindow[]> {
  if (!entries.length) return failure("Add at least one session.");
  if (entries.length > MAX_PROPOSAL_SESSIONS)
    return failure(`A proposal can have at most ${MAX_PROPOSAL_SESSIONS} sessions.`);
  const today = localDateAt(now, timezone);
  if (today === null) return failure("Facility timezone is unavailable.");
  const lastAllowed = today + MAX_SCHEDULE_DAYS_AHEAD * DAY_MS;
  const windows: ScheduledWindow[] = [];
  for (const entry of entries) {
    const day = civilDay(entry.localStart.slice(0, 10));
    if (day === null || day < today || day > lastAllowed)
      return failure("Sessions must be within the next 180 days.");
    const minutes = durationMinutes(entry.duration);
    if (minutes === null) return failure("Every session needs a duration from 15 to 480 minutes.");
    if (!entry.courtId && entry.courtIds === undefined)
      return failure("Choose a court or No court required for every session.");
    const instant = entry.exactStart && toZonedDateTimeInput(entry.exactStart, timezone) === entry.localStart
      ? entry.exactStart : fromZonedDateTimeInput(entry.localStart, timezone);
    if (!instant) return failure("A session has an invalid local date or time.");
    const start = Date.parse(instant);
    if (!Number.isFinite(start)) return failure("A session has an invalid local date or time.");
    windows.push({ id: entry.id, localStart: entry.localStart, starts_at: new Date(start).toISOString(),
      ends_at: new Date(start + minutes * 60_000).toISOString(),
      court_ids: entry.courtIds ?? (entry.courtId === "none" ? [] : [entry.courtId]) });
  }
  windows.sort((left, right) => Date.parse(left.starts_at) - Date.parse(right.starts_at) || left.id.localeCompare(right.id));
  if (windows.some((window, index) => index > 0 && Date.parse(windows[index - 1].ends_at) > Date.parse(window.starts_at)))
    return failure("Sessions overlap. Move or remove a session before sending.");
  return success(windows);
}
